import {Tooltip} from '@wandb/weave/components/Tooltip';
import clsx from 'clsx';
import React, {
  type FC,
  type ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import {Alias, type AliasItem} from '../../ArtifactAlias';
import {
  type ModifiedTag,
  TagAssignment,
  type TagAssignmentProps,
} from '../../TagAssignment';

// Default styling for the `+N` indicator (moon pill)
const OVERFLOW_INDICATOR_CLASS =
  'inline-flex h-24 flex-shrink-0 items-center rounded-full bg-wb-surface-selected px-8 text-sm text-wb-text-secondary';

type OverflowContainerProps<T> = {
  items: T[];
  renderItem: (item: T) => ReactNode;
  renderOverflowTooltipItem?: (item: T) => ReactNode;
  overflowIndicatorClassName?: string;
  gap?: number;
  trailingElement?: ReactNode;
  trailingElementClassName?: string;
};

/**
 * Generic overflow container that renders as many items as fit in the
 * available width, with a "+N" indicator for the rest.
 *
 * Responds to container and window resize events and uses actual DOM
 * measurements for accurate width calculations.
 */
export function OverflowContainer<T>({
  items,
  renderItem,
  renderOverflowTooltipItem,
  overflowIndicatorClassName = OVERFLOW_INDICATOR_CLASS,
  gap = 0,
  trailingElement,
  trailingElementClassName,
}: OverflowContainerProps<T>) {
  const containerRef = useRef<HTMLDivElement>(null);
  const hiddenItemsRef = useRef<HTMLDivElement>(null);
  const overflowIndicatorRef = useRef<HTMLSpanElement>(null);
  const trailingElementRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(items.length);
  const [overflowCount, setOverflowCount] = useState(0);

  const calculateVisibleItems = useCallback(() => {
    if (
      !containerRef.current ||
      !hiddenItemsRef.current ||
      items.length === 0
    ) {
      return;
    }

    const containerWidth = containerRef.current.offsetWidth;

    const hiddenItemElements = Array.from(
      hiddenItemsRef.current.children
    ).filter(child =>
      child.classList.contains('hidden-overflow-item')
    ) as HTMLElement[];

    if (hiddenItemElements.length !== items.length) {
      return;
    }

    const overflowIndicatorWidth =
      overflowIndicatorRef.current?.offsetWidth || 0;
    const trailingElementWidth = trailingElementRef.current?.offsetWidth || 0;
    // `gap-*` applies between every flex child. Reserve the gap before the
    // trailing control as well as its width, otherwise a fully occupied row
    // can push the control a few pixels past its fixed right-side slot.
    const trailingSpace = trailingElement
      ? trailingElementWidth + (items.length > 0 ? gap : 0)
      : 0;

    let currentWidth = 0;
    let count = 0;

    for (let i = 0; i < hiddenItemElements.length; i++) {
      const itemWidth = hiddenItemElements[i].offsetWidth;
      const gapBefore = count > 0 ? gap : 0;

      const remainingItems = items.length - (i + 1);
      const overflowSpace =
        (remainingItems > 0 ? overflowIndicatorWidth + gap : 0) + trailingSpace;

      if (
        currentWidth + gapBefore + itemWidth + overflowSpace <=
        containerWidth
      ) {
        currentWidth += gapBefore + itemWidth;
        count++;
      } else {
        break;
      }
    }

    // Ensure we show at least one item if possible
    if (count === 0 && items.length > 0) {
      const firstItemWidth = hiddenItemElements[0]?.offsetWidth || 0;
      if (
        firstItemWidth + overflowIndicatorWidth + trailingSpace <=
        containerWidth
      ) {
        count = 1;
      }
    }

    const newOverflowCount = Math.max(0, items.length - count);

    if (visibleCount !== count || overflowCount !== newOverflowCount) {
      setVisibleCount(count);
      setOverflowCount(newOverflowCount);
    }
  }, [items, visibleCount, overflowCount, gap, trailingElement]);

  // Initial calculation before paint. useLayoutEffect (rather than useEffect +
  // requestAnimationFrame) commits the visible/overflow counts in the same frame
  // as the items render. Under React 18 concurrent mode the rAF setState would
  // otherwise be batched/deferred, leaving a transient "all items expanded"
  // frame before collapsing to the "+N" indicator. The hidden measuring
  // container always renders every item, so offsetWidth reads are valid here.
  useLayoutEffect(() => {
    calculateVisibleItems();
  }, [calculateVisibleItems]);

  // Recalculate on window resize and container resize
  useEffect(() => {
    let resizeTimeout: NodeJS.Timeout;
    const debouncedResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(calculateVisibleItems, 100);
    };

    window.addEventListener('resize', debouncedResize);

    let resizeObserver: ResizeObserver | null = null;
    if (containerRef.current) {
      resizeObserver = new ResizeObserver(debouncedResize);
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      clearTimeout(resizeTimeout);
      window.removeEventListener('resize', debouncedResize);
      resizeObserver?.disconnect();
    };
  }, [calculateVisibleItems]);

  if (items.length === 0 && trailingElement == null) {
    return null;
  }

  const hiddenItems = items.slice(visibleCount);
  const tooltipContent =
    hiddenItems.length > 0 ? (
      <div className="flex max-w-[360px] flex-wrap gap-4">
        {hiddenItems.map((item, i) => (
          // oxlint-disable-next-line react/no-array-index-key -- https://oxc.rs/docs/guide/usage/linter/rules/react/no-array-index-key.html
          <div key={i} className="flex-shrink-0 leading-none">
            {(renderOverflowTooltipItem ?? renderItem)(item)}
          </div>
        ))}
      </div>
    ) : null;

  return (
    <>
      {/* Visible container */}
      <div
        ref={containerRef}
        className={clsx(
          'flex items-center w-full overflow-hidden',
          gap && `gap-${gap}`
        )}>
        {items.slice(0, visibleCount).map((item, i) => (
          // oxlint-disable-next-line react/no-array-index-key -- https://oxc.rs/docs/guide/usage/linter/rules/react/no-array-index-key.html
          <div key={i} className="flex-shrink-0 leading-none">
            {renderItem(item)}
          </div>
        ))}
        {overflowCount > 0 && (
          <Tooltip
            position="top center"
            content={tooltipContent}
            contentClassName="bg-wb-surface-sunken text-wb-text-primary dark:bg-moon-900 dark:text-moon-200"
            trigger={
              <span
                className={`inline-flex flex-shrink-0 items-center ${overflowIndicatorClassName}`}>
                +{overflowCount}
              </span>
            }
          />
        )}
        {trailingElement != null && (
          <div
            ref={trailingElementRef}
            className={clsx('flex-shrink-0', trailingElementClassName)}>
            {trailingElement}
          </div>
        )}
      </div>

      {/* Hidden container for measuring actual item widths */}
      <div
        ref={hiddenItemsRef}
        className="absolute invisible pointer-events-none top-[-9999px] left-[-9999px] flex items-center"
        aria-hidden="true">
        {items.map((item, i) => (
          <div
            // oxlint-disable-next-line react/no-array-index-key -- https://oxc.rs/docs/guide/usage/linter/rules/react/no-array-index-key.html
            key={i}
            className="hidden-overflow-item flex-shrink-0 leading-none">
            {renderItem(item)}
          </div>
        ))}
        <span
          ref={overflowIndicatorRef}
          className={`inline-flex flex-shrink-0 items-center ${overflowIndicatorClassName}`}>
          +99
        </span>
      </div>
    </>
  );
}

// Pre-configured wrappers for the two overflow use cases

type OverflowTagsProps = Partial<
  Pick<TagAssignmentProps, 'showColor' | 'size' | 'canDelete' | 'disableHover'>
> & {
  tags: ModifiedTag[];
};

export const OverflowTags: FC<OverflowTagsProps> = ({
  tags,
  showColor = true,
  size = 'small',
  canDelete = false,
  disableHover = true,
}) => {
  const renderTag = useCallback(
    (tag: ModifiedTag) => (
      <TagAssignment
        tag={tag}
        showColor={showColor}
        size={size}
        canDelete={canDelete}
        disableHover={disableHover}
      />
    ),
    [showColor, size, canDelete, disableHover]
  );

  return (
    <OverflowContainer
      items={tags}
      renderItem={renderTag}
      overflowIndicatorClassName={`ui label run-tag ${size} tag-teal-light flex-shrink-0`}
    />
  );
};

type OverflowAliasesProps = {
  aliases: AliasItem[];
  onClick?: (alias: string) => void;
  size?: 'large' | 'medium' | 'small';
};

export const OverflowAliases: FC<OverflowAliasesProps> = ({
  aliases,
  onClick,
  size = 'large',
}) => {
  const renderAlias = useCallback(
    (item: AliasItem) => (
      <Alias
        alias={{alias: item.alias}}
        isProtected={item.isProtected}
        size={size}
        onClick={onClick ? () => onClick(item.alias) : undefined}
      />
    ),
    [onClick, size]
  );

  return (
    <OverflowContainer
      items={aliases}
      renderItem={renderAlias}
      overflowIndicatorClassName={`ui label run-tag ${size} tag-sienna-light flex-shrink-0`}
    />
  );
};
